export const SERVICE_START_FALLBACK = 'Usually starts within 5 business days';

function calendarDate(value) {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return;
  const date = new Date(`${value}T12:00:00Z`);
  if (Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value) return date;
}

/** The caller supplies its reference time; no date is calculated at build time. */
export function serviceStartPromise(value, reference) {
  if (!value || typeof value !== 'object' || !(reference instanceof Date) ||
      !Number.isFinite(reference.getTime())) {
    return SERVICE_START_FALLBACK;
  }
  const rule = value;
  const from = calendarDate(rule.validFrom);
  const through = calendarDate(rule.validThrough);
  if (!Number.isSafeInteger(rule.leadTimeBusinessDays) || rule.leadTimeBusinessDays < 0 ||
      typeof rule.timeZone !== 'string' || !rule.timeZone || !from || !through || from > through ||
      !Array.isArray(rule.holidays) || !rule.holidays.every(day => calendarDate(day))) {
    return SERVICE_START_FALLBACK;
  }

  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: rule.timeZone, year: 'numeric', month: '2-digit', day: '2-digit',
    }).formatToParts(reference);
    const part = (type) => parts.find(item => item.type === type)?.value;
    const day = calendarDate(`${part('year')}-${part('month')}-${part('day')}`);
    if (!day || day < from || day > through) return SERVICE_START_FALLBACK;

    let remaining = rule.leadTimeBusinessDays;
    if (remaining > (through.getTime() - day.getTime()) / 86400000) return SERVICE_START_FALLBACK;
    const holidays = new Set(rule.holidays);
    const isBusinessDay = () => day.getUTCDay() !== 0 && day.getUTCDay() !== 6 &&
      !holidays.has(day.toISOString().slice(0, 10));
    while (remaining > 0 || !isBusinessDay()) {
      day.setUTCDate(day.getUTCDate() + 1);
      if (day > through) return SERVICE_START_FALLBACK;
      if (remaining > 0 && isBusinessDay()) remaining--;
    }
    const formatted = new Intl.DateTimeFormat('en-US', {
      timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric',
    }).format(day);
    return `Service can start as soon as ${formatted}`;
  } catch {
    return SERVICE_START_FALLBACK;
  }
}

// Read only public calendar data; signing-page code remains in this bundle.
export async function loadServiceStartRule() {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 1500);
  try {
    const response = await fetch('https://cabanapools.com/service-start.json', {
      credentials: 'omit',
      referrerPolicy: 'no-referrer',
      cache: 'no-cache',
      signal: controller.signal,
    });
    if (!response.ok) return null;
    const config = await response.json();
    if (config?.version === 1 && config.experimentEnabled === false) return undefined;
    return config?.version === 1 ? config.brands?.cabana ?? null : null;
  } catch {
    return null;
  } finally {
    clearTimeout(timeout);
  }
}

// The marker is written only after a visible eligible marketing exposure.
export function serviceStartParticipant(cookies, sid, website, now = Date.now()) {
  if (website !== 'cabanapools.com' || !sid || typeof cookies !== 'string') return false;
  try {
    const raw = cookies.split('; ').find(item => item.startsWith('_cabana_start='));
    const marker = JSON.parse(decodeURIComponent(raw?.slice('_cabana_start='.length) || ''));
    const age = now - marker.exposedAt;
    return marker.version === 1 && marker.arm === 'treatment' && marker.sid === sid &&
      Number.isFinite(marker.exposedAt) && age >= 0 && age < 30 * 86400000;
  } catch {
    return false;
  }
}

export function serviceStartConfirmation(rule, reference, justSigned) {
  return justSigned ? serviceStartPromise(rule, reference) : SERVICE_START_FALLBACK;
}
